/**
 * **Langganan** — apa yang dibeli tenant ini, dan sampai kapan.
 *
 * ### ⛔⛔ Layar ini SENGAJA tanpa tombol apa pun
 * Provisioning (`CREATE DATABASE` + migrasi) tetap milik OSB, dipicu dari konsol operator. Memberi
 * tenant tombol aktivasi swalayan sempat dipertimbangkan; yang membatalkannya: gerbangnya jadi
 * bergantung pada **keadaan** ("pending & langganan aktif") alih-alih pada **scope**, dan gerbang
 * berbasis keadaan gagal senyap saat keadaannya salah dihitung.
 *
 * ⚠️ Karena itu layar ini harus **mengatakan ke mana harus menghubungi** saat ada yang perlu
 * diubah. Layar informasi yang tak menyebut jalan keluar membuat orang menekan-nekan mencari
 * tombol yang memang tak ada.
 *
 * ### ⛔ Tak ada harga di sini
 * Harga adalah urusan penagihan, dan penagihan bukan buku besar (§0) — control-plane POS pun
 * menuliskannya sendiri: *"tak ada kolom uang di sini"*. Yang ditampilkan **hak pakai** dan **masa
 * berlaku**.
 */
import { Alert, Card, Descriptions, Empty, Space, Table, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Subscription } from '../api/client';

/** Sisa hari sampai kedaluwarsa. `null` bila tak ada tanggal akhir (langganan tanpa batas). */
function sisaHari(validTo: string | null): number | null {
  if (validTo === null) return null;
  const akhir = new Date(`${validTo}T00:00:00Z`).getTime();
  return Math.ceil((akhir - Date.now()) / 86_400_000);
}

export function Subscription() {
  const { t } = useTranslation();
  const [data, setData] = useState<Subscription | null>(null);
  const [gagal, setGagal] = useState(false);

  useEffect(() => {
    void api.subscription().then(setData).catch(() => { setGagal(true); });
  }, []);

  if (gagal) {
    return <Alert type="error" showIcon title={t('subscription.loadFailed')} data-testid="langganan-galat" />;
  }
  if (data === null) return <Empty description={t('common.loading')} />;

  const sisa = sisaHari(data.validTo);

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="subscription">
      {data.plan === null ? (
        /*
         * ⛔ "Belum berlangganan" dikatakan APA ADANYA. Tenant yang baru di-provisioning memang
         * belum punya paket sampai OSB menugaskannya; kotak kosong tanpa kalimat terbaca seperti
         * kerusakan, dan yang membacanya akan menelepon menanyakan hal yang sebenarnya normal.
         */
        <Alert type="warning" showIcon data-testid="langganan-kosong"
               title={t('subscription.none')} description={t('subscription.contactOsb')} />
      ) : (
        <>
          {/*
            ⚠️ Peringatan kedaluwarsa muncul SEBELUM habis, bukan sesudah. Langganan yang baru
            diberitahukan pada hari terakhir adalah langganan yang diperpanjang terlambat — dan
            yang berhenti sementara adalah kasir, bukan orang yang menandatangani kontrak.
          */}
          {sisa !== null && sisa <= 30 && (
            <Alert
              type={sisa <= 7 ? 'error' : 'warning'}
              showIcon
              data-testid="langganan-segera-habis"
              title={sisa < 0
                ? t('subscription.expired', { n: Math.abs(sisa) })
                : t('subscription.expiring', { n: sisa })}
              description={t('subscription.contactOsb')}
            />
          )}

          <Card title={t('subscription.title')} data-testid="langganan-kartu">
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('subscription.plan')}>
                <b data-testid="langganan-paket">{data.plan.name}</b>{' '}
                <Tag>{data.plan.code}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('outlets.status')}>
                <Tag color={data.status === 'active' ? 'green' : 'orange'}
                     data-testid="langganan-status">
                  {data.status ?? '—'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('subscription.validFrom')}>
                {data.validFrom ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('subscription.validTo')}>
                <span data-testid="langganan-berakhir">{data.validTo ?? t('subscription.noEnd')}</span>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title={t('subscription.features')} data-testid="langganan-fitur">
            {data.entitlements.length === 0 ? (
              /* ⚠️ Paket tanpa hak pakai bukan galat — tapi ia juga tak membuka apa pun, dan itu
                 harus terlihat sebelum orang bertanya kenapa fiturnya tak menyala. */
              <Empty description={t('subscription.noFeature')} data-testid="langganan-tanpa-fitur" />
            ) : (
              <Table
                size="small"
                pagination={false}
                rowKey={(r) => r.featureCode}
                dataSource={[...data.entitlements]}
                columns={[
                  {
                    title: t('subscription.feature'),
                    dataIndex: 'featureCode',
                    render: (v: string) => <span data-testid={`fitur-${v}`}>{v}</span>,
                  },
                  {
                    title: t('subscription.limit'),
                    dataIndex: 'limit',
                    width: 160,
                    /* ⭐ `null` = TANPA BATAS, bukan nol. Menampilkannya sebagai "0" akan terbaca
                       sebagai "tak boleh sama sekali" — kebalikan persis dari artinya. */
                    render: (v: number | null) =>
                      v === null ? <Tag color="blue">{t('subscription.unlimited')}</Tag> : v,
                  },
                ]}
              />
            )}
          </Card>
        </>
      )}

      {/* ⛔ Tak ada tombol di layar ini — jalan keluarnya disebut, bukan disembunyikan. */}
      <Typography.Text type="secondary" data-testid="langganan-catatan">
        {t('subscription.readOnly')}
      </Typography.Text>
    </Space>
  );
}
